<template>
  <main>
    <Error v-if="errorNotFound" :error="errorNotFound" />
    <SearchPage v-else-if="slug === 'search'" :key="slug" />
    <template v-else>
      <CArticlePage
        v-if="
          pageTemplate === 'page-detail' ||
          pageTemplate === 'services-detail' ||
          pageTemplate === 'services-category'
        "
        :key="slug"
        :template="pageTemplate"
        @error-not-found="errorArticlePage"
      />
      <CDoctorsList
        v-if="pageTemplate === 'doctors-category'"
        :key="slug"
        @error-not-found="errorArticlePage"
      />
      <CDoctorDetailPage
        v-if="pageTemplate === 'doctors-detail'"
        :key="slug"
        @error-not-found="errorArticlePage"
      />
      <CServicesList
        v-if="pageTemplate === 'services-list'"
        :key="slug"
        @error-not-found="errorArticlePage"
      />
      <CIndexPage
        v-if="pageTemplate === 'unique-placeholder'"
        :key="slug"
        @error-not-found="errorArticlePage"
      />
      <CSectionPage
        v-if="pageTemplate === 'page-list' || pageTemplate === 'page-list-sidebar'"
        :key="slug"
        :is-show-filters="data.is_filters_displayed"
        :id="data.id"
        @error-not-found="errorArticlePage"
      />
    </template>
  </main>
</template>

<script setup lang="ts">
import {
  createError,
  computed,
  ref,
  watch,
  useRoute,
  useAsyncData,
  useRequestEvent,
  setResponseStatus,
  useHead,
} from '#imports';
import Error from '~/error.vue';
import { type PageResult, type PageTemplate } from '~/types/pages';
import type { ErrorResponse } from '~/server/utils/serverFetch';
import { isErrorResponse } from '~/server/utils/serverError';
import SearchPage from '~/components/pages/SearchPage.vue';
import CDoctorsList from '~/components/pages/doctorsList/CDoctorsList.vue';
import CDoctorDetailPage from '~/components/pages/doctorDetail/CDoctorDetailPage.vue';
import CServicesList from '~/components/pages/servicesList/CServicesList.vue';
import CIndexPage from '~/components/pages/indexPage/CIndexPage.vue';
import CSectionPage from '~/components/pages/CSectionPage.vue';
import CArticlePage from '~/components/pages/articlePage/CArticlePage.vue';
import { useCanonical } from '~/composables/useCanonical';

const route = useRoute();

const slug = computed(() => {
  if (route.path === '/') {
    return 'index';
  }

  if (route.params && route.params.chapters) {
    return (route.params.chapters as string[]).join('/');
  }
});

const pageTemplate = ref<PageTemplate>();
const errorNotFound = ref<ErrorResponse>();

const { data, error } = await useAsyncData<ErrorResponse | PageResult>(
  `getTemplateResponse:${slug.value}`,
  () => {
    return $fetch(`/api/pages/slug`, {
      method: 'GET',
      params: {
        slug: slug.value,
      },
    });
  },
  {
    pick: ['template', 'status', 'data', 'id', 'is_filters_displayed'],
    watch: [slug],
  },
);

const setErrorNotFound = (statusCode: number = 404, message: string = 'Страница не найдена.') => {
  pageTemplate.value = '404-error';

  errorNotFound.value = {
    statusCode: statusCode || 404,
    message,
  };

  if (process.server) {
    const event = useRequestEvent();
    if (event) {
      setResponseStatus(event, statusCode || 404, 'Not Found');
    }
  }
};

const errorArticlePage = (statusCode: number = 404, message: string = 'Страница не найдена.') => {
  setErrorNotFound(statusCode, message);
};

watch(
  data,
  value => {
    errorNotFound.value = undefined;

    if (!value) {
      return;
    }

    if (!isErrorResponse(value)) {
      if (value.template === '404-error') {
        setErrorNotFound(404, 'Страница не найдена.');
      } else {
        pageTemplate.value = value.template;
      }
    } else {
      setErrorNotFound(value.status || 404, value.data?.detail || 'Страница не найдена.');
    }
  },
  { immediate: true },
);

watch(error, value => {
  if (value) {
    setErrorNotFound(404, 'Страница не найдена.');
  }
});
const isErrorPage = computed(() => errorNotFound.value !== undefined);
const isSearch = computed(() => slug.value === 'search');
// каноникл не должен отображатся на страницах ошибок и на поиске
const shouldSetCanonical = computed(() => !isErrorPage.value && !isSearch.value);

const canonical = useCanonical();

const canonicalHref = computed(() => (shouldSetCanonical.value ? canonical.value : undefined));

useHead(() => ({
  link: [
    {
      key: 'canonical',
      rel: 'canonical',
      href: canonicalHref.value,
    },
  ],
}));
</script>
